<script lang="ts">
	import { Stack, Select, Input, Text, Collapse } from '@Shelly/shelly-design-system';
	import { flowEditorState } from '../../../../stores/flow_editor';
	import { user_settings } from '../../../../stores';

	export let nodeId: string;

	// Get node data
	$: node = $flowEditorState.nodes.find((n) => n.id === nodeId);
	$: nodeData = (node?.data || {}) as any;

	// Sun type options
	const sunTypes = [
		{ value: 'sunrise', title: 'Sunrise' },
		{ value: 'sunset', title: 'Sunset' },
	];

	// Before/After options
	const beforeAfterOptions = [
		{ value: 'after', title: 'After' },
		{ value: 'before', title: 'Before' },
	];

	// Current values - make reactive to nodeData changes
	// `sunType` is the canonical inspector field; `type` is the legacy spread from SceneToFlow
	$: sunType = nodeData.sunType ?? nodeData.type ?? 'sunrise';
	$: beforeAfter = nodeData.beforeAfter || 'after';
	$: hours = nodeData.hours || 0;
	// `minutes` is the canonical inspector field; `mins` is the legacy spread from SceneToFlow
	$: minutes = nodeData.minutes ?? nodeData.mins ?? 0;

	// Check if location is configured
	$: hasLocation = typeof $user_settings?.weather_station?.l !== 'undefined';
	$: locationId = $user_settings?.weather_station?.l || '';

	// Calculate total offset in minutes
	$: offsetMinutes = hours * 60 + minutes;

	// Update node when values change
	function updateNode() {
		if (!node) return;

		const updatedData = {
			...nodeData,
			sceneData: undefined,
			originalCondition: undefined,
			sunType,
			beforeAfter,
			hours,
			minutes,
			offsetMinutes,
			locid: locationId,
			label: generateLabel(),
		};

		flowEditorState.updateNode(nodeId, { data: updatedData });
	}

	// Generate label based on settings
	function generateLabel(): string {
		const typeLabel = sunType === 'sunrise' ? 'Sunrise' : 'Sunset';
		const whenLabel = beforeAfter === 'after' ? 'after' : 'before';

		if (hours === 0 && minutes === 0) {
			return `At ${typeLabel}`;
		}

		const parts: string[] = [];
		if (hours > 0) parts.push(`${hours}h`);
		if (minutes > 0) parts.push(`${minutes}m`);
		const timeStr = parts.join(' ');

		return `${timeStr} ${whenLabel} ${typeLabel}`;
	}

	// Validate inputs
	function validateHours(val: number) {
		hours = Math.max(0, Math.min(23, val));
	}

	function validateMinutes(val: number) {
		minutes = Math.max(0, Math.min(59, val));
	}

	function handleHoursChange(e: any) {
		validateHours(parseInt(e?.target?.value || '0', 10) || 0);
		updateNode();
	}

	function handleMinutesChange(e: any) {
		validateMinutes(parseInt(e?.target?.value || '0', 10) || 0);
		updateNode();
	}
</script>

<div class="sunrise-sunset-inspector">
	<Stack direction="row" gap={16}>
		<Text variant="h3">Sunrise/Sunset Condition</Text>

		<Collapse title="Time Settings" expanded={true} id="sunrise-sunset-time-settings">
			<Stack direction="row" gap={12}>
				<Select
					aria-label="select-sun-type"
					id="select-sun-type"
					icon=""
					label="Type"
					value={sunType}
					options={sunTypes}
					on:change={(e) => {
						const detail = e.detail;
						sunType =
							typeof detail === 'string'
								? detail
								: detail && typeof detail === 'object' && 'value' in detail
									? detail.value
									: detail;
						updateNode();
					}}
				/>

				<Select
					aria-label="select-before-after"
					id="select-before-after"
					icon=""
					label="Trigger"
					value={beforeAfter}
					options={beforeAfterOptions}
					on:change={(e) => {
						const detail = e.detail;
						beforeAfter =
							typeof detail === 'string'
								? detail
								: detail && typeof detail === 'object' && 'value' in detail
									? detail.value
									: detail;
						updateNode();
					}}
				/>

				<Stack direction="row" gap={12}>
					<Input
						aria-label="input-hours"
						id="input-hours"
						type="number"
						label="Hours"
						value={hours}
						on:change={handleHoursChange}
						min="0"
						max="23"
						placeholder="0"
					/>

					<Input
						aria-label="input-minutes"
						id="input-minutes"
						type="number"
						label="Minutes"
						value={minutes}
						on:change={handleMinutesChange}
						min="0"
						max="59"
						placeholder="0"
					/>
				</Stack>
			</Stack>
		</Collapse>

		<div class="info-box">
			<i class="fas fa-info-circle"></i>
			<Stack direction="row" gap={8}>
				<Text variant="body2">
					Triggers the scene at the specified time relative to sunrise or sunset based on your
					location.
				</Text>
				<Text variant="body2">
					<strong>Example:</strong> "30m before Sunset" triggers 30 minutes before sunset.
				</Text>
				<Text variant="body2">Premium feature.</Text>
			</Stack>
		</div>
	</Stack>
</div>

<style>
	.sunrise-sunset-inspector {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.warning-box,
	.info-box {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 12px;
		border-radius: 6px;
		background: rgba(245, 158, 11, 0.1);
		border: 1px solid rgba(245, 158, 11, 0.3);
	}

	.info-box {
		background: rgba(59, 130, 246, 0.1);
		border-color: rgba(59, 130, 246, 0.3);
	}

	.warning-box i,
	.info-box i {
		color: #f59e0b;
		margin-top: 2px;
		flex-shrink: 0;
	}

	.info-box i {
		color: #3b82f6;
	}
</style>
